package com.iptvplayer.shared.data.local.cache

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.coroutines.mapToOne
import com.iptvplayer.shared.data.local.AppPreferences
import com.iptvplayer.shared.data.local.db.DatabaseDriverFactory
import com.iptvplayer.shared.db.ChannelEntity
import com.iptvplayer.shared.db.IPTVDatabase
import com.iptvplayer.shared.db.IptvSourceEntity
import com.iptvplayer.shared.db.SeriesEntity
import com.iptvplayer.shared.db.VodItemEntity
import com.iptvplayer.shared.domain.model.Channel
import com.iptvplayer.shared.domain.model.IptvSource
import com.iptvplayer.shared.domain.model.SeriesItem
import com.iptvplayer.shared.domain.model.StreamType
import com.iptvplayer.shared.domain.model.VodItem
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.withContext
import kotlinx.datetime.Clock

/**
 * Local persistent cache for IPTV content (sources + channels + VOD) backed by
 * SQLDelight. Entirely fail-safe: if the platform has no SQLite driver (e.g. the
 * JS target throws) or any DB operation fails, it degrades to "no cache" so the
 * repository simply falls back to the network.
 */
class ContentCache(driverFactory: DatabaseDriverFactory) {

    private val db: IPTVDatabase? = try {
        IPTVDatabase(driverFactory.createDriver())
    } catch (e: Throwable) {
        null
    }

    init {
        // Give AppPreferences a persistent backend on platforms with SQLite
        // (three tiny KV reads at startup; web stays session-only).
        db?.let { database ->
            AppPreferences.bind(
                read = { key ->
                    runCatching {
                        database.cacheMetaQueries.selectValue(key).executeAsOneOrNull()
                    }.getOrNull()
                },
                write = { key, value ->
                    runCatching { database.cacheMetaQueries.upsert(key, value) }
                }
            )
        }
    }

    /**
     * True when a real SQLite database is available on this platform. The
     * repository uses this to decide between DB-backed windows (Android/iOS/
     * desktop) and the in-RAM catalog fallback (JS/web has no driver).
     */
    val isAvailable: Boolean get() = db != null

    private fun nowMs() = Clock.System.now().toEpochMilliseconds()

    private fun lastSyncedAtMs(): Long =
        runCatching {
            db?.cacheMetaQueries?.selectValue(KEY_SYNCED_AT)?.executeAsOneOrNull()?.toLongOrNull()
        }.getOrNull() ?: 0L

    /** True when cached content exists and is younger than [ttlMs]. */
    suspend fun isFresh(ttlMs: Long): Boolean = withContext(Dispatchers.Default) {
        val database = db ?: return@withContext false
        runCatching {
            val last = lastSyncedAtMs()
            val age = nowMs() - last
            // LIMIT-1 existence probes — don't materialize whole tables just to
            // check non-emptiness. Any content type counts: a VOD-only or
            // series-only provider has zero channels, and requiring channels
            // here used to force a full network re-sync on every launch.
            last > 0L && age in 0 until ttlMs && (
                database.channelQueries.pageFiltered(null, null, 1L, 0L).executeAsList().isNotEmpty() ||
                    database.vodItemQueries.pageFiltered(null, null, 1L, 0L).executeAsList().isNotEmpty() ||
                    database.seriesQueries.pageFiltered(null, null, 1L, 0L).executeAsList().isNotEmpty()
            )
        }.getOrDefault(false)
    }

    suspend fun loadSources(): List<IptvSource> = withContext(Dispatchers.Default) {
        runCatching { db?.iptvSourceQueries?.selectAll()?.executeAsList()?.mapNotNull { it.toDomain() } }
            .getOrNull() ?: emptyList()
    }

    suspend fun loadChannels(): List<Channel> = withContext(Dispatchers.Default) {
        runCatching { db?.channelQueries?.selectAll()?.executeAsList()?.map { it.toDomain() } }
            .getOrNull() ?: emptyList()
    }

    suspend fun loadVod(): List<VodItem> = withContext(Dispatchers.Default) {
        runCatching { db?.vodItemQueries?.selectAll()?.executeAsList()?.map { it.toDomain() } }
            .getOrNull() ?: emptyList()
    }

    suspend fun loadSeries(): List<SeriesItem> = withContext(Dispatchers.Default) {
        runCatching { db?.seriesQueries?.selectAll()?.executeAsList()?.map { it.toDomain() } }
            .getOrNull() ?: emptyList()
    }

    // ──────────────────── Reactive paged reads (Browse) ────────────────────
    // Each window query re-emits whenever the underlying table changes (e.g. a
    // sync writes rows), so the UI stays live without holding the catalog in RAM.
    // A NULL group/query disables that filter, so one query serves all / by
    // category / search. We always read from offset 0 with a growing limit so the
    // visible window is a single contiguous list the UI can diff by key.

    fun channelsWindow(group: String?, query: String?, limit: Long): Flow<List<Channel>> {
        val database = db ?: return flowOf(emptyList())
        return database.channelQueries.pageFiltered(group, query, limit, 0L)
            .asFlow().mapToList(Dispatchers.Default)
            .map { rows -> rows.map { it.toDomain() } }
    }

    fun channelsCount(group: String?, query: String?): Flow<Long> {
        val database = db ?: return flowOf(0L)
        return database.channelQueries.countFiltered(group, query)
            .asFlow().mapToOne(Dispatchers.Default)
    }

    fun channelGroups(): Flow<List<String>> {
        val database = db ?: return flowOf(emptyList())
        return database.channelQueries.selectGroups()
            .asFlow().mapToList(Dispatchers.Default)
            .map { it.filterNotNull() }
    }

    fun vodWindow(group: String?, query: String?, limit: Long): Flow<List<VodItem>> {
        val database = db ?: return flowOf(emptyList())
        return database.vodItemQueries.pageFiltered(group, query, limit, 0L)
            .asFlow().mapToList(Dispatchers.Default)
            .map { rows -> rows.map { it.toDomain() } }
    }

    fun vodCount(group: String?, query: String?): Flow<Long> {
        val database = db ?: return flowOf(0L)
        return database.vodItemQueries.countFiltered(group, query)
            .asFlow().mapToOne(Dispatchers.Default)
    }

    fun vodGroups(): Flow<List<String>> {
        val database = db ?: return flowOf(emptyList())
        return database.vodItemQueries.selectGroups()
            .asFlow().mapToList(Dispatchers.Default)
            .map { it.filterNotNull() }
    }

    fun seriesWindow(group: String?, query: String?, limit: Long): Flow<List<SeriesItem>> {
        val database = db ?: return flowOf(emptyList())
        return database.seriesQueries.pageFiltered(group, query, limit, 0L)
            .asFlow().mapToList(Dispatchers.Default)
            .map { rows -> rows.map { it.toDomain() } }
    }

    fun seriesCount(group: String?, query: String?): Flow<Long> {
        val database = db ?: return flowOf(0L)
        return database.seriesQueries.countFiltered(group, query)
            .asFlow().mapToOne(Dispatchers.Default)
    }

    fun seriesGroups(): Flow<List<String>> {
        val database = db ?: return flowOf(emptyList())
        return database.seriesQueries.selectGroups()
            .asFlow().mapToList(Dispatchers.Default)
            .map { it.filterNotNull() }
    }

    // ──────────────────── One-shot page reads (deep scroll) ────────────────────
    // The reactive windows above re-read and re-map the WHOLE window on every
    // page growth (O(n²) across a long scroll). Deep pages are fetched once via
    // these and appended in the view model instead.

    suspend fun channelsPage(group: String?, query: String?, limit: Long, offset: Long): List<Channel> =
        withContext(Dispatchers.Default) {
            val database = db ?: return@withContext emptyList()
            runCatching {
                database.channelQueries.pageFiltered(group, query, limit, offset)
                    .executeAsList().map { it.toDomain() }
            }.getOrDefault(emptyList())
        }

    suspend fun vodPage(group: String?, query: String?, limit: Long, offset: Long): List<VodItem> =
        withContext(Dispatchers.Default) {
            val database = db ?: return@withContext emptyList()
            runCatching {
                database.vodItemQueries.pageFiltered(group, query, limit, offset)
                    .executeAsList().map { it.toDomain() }
            }.getOrDefault(emptyList())
        }

    suspend fun seriesPage(group: String?, query: String?, limit: Long, offset: Long): List<SeriesItem> =
        withContext(Dispatchers.Default) {
            val database = db ?: return@withContext emptyList()
            runCatching {
                database.seriesQueries.pageFiltered(group, query, limit, offset)
                    .executeAsList().map { it.toDomain() }
            }.getOrDefault(emptyList())
        }

    // ──────────────────── One-shot reads (no RAM catalog needed) ────────────────────

    /** True when any series rows are persisted (used to skip the big lazy re-fetch). */
    suspend fun hasSeries(): Boolean = withContext(Dispatchers.Default) {
        val database = db ?: return@withContext false
        runCatching {
            database.seriesQueries.pageFiltered(null, null, 1L, 0L).executeAsList().isNotEmpty()
        }.getOrDefault(false)
    }

    suspend fun findSeries(sourceId: String, seriesId: Long): SeriesItem? =
        withContext(Dispatchers.Default) {
            val database = db ?: return@withContext null
            runCatching {
                database.seriesQueries.selectByKey(sourceId, seriesId).executeAsOneOrNull()?.toDomain()
            }.getOrNull()
        }

    suspend fun findChannel(id: String): Channel? = withContext(Dispatchers.Default) {
        val database = db ?: return@withContext null
        runCatching {
            database.channelQueries.selectById(id).executeAsOneOrNull()?.toDomain()
        }.getOrNull()
    }

    /**
     * Previous/next live channel for zapping, in name order (the Browse order).
     * Prefers siblings in the current channel's group; falls back to the whole
     * catalog when the group has no other channels. Wraps around at the ends.
     */
    suspend fun zapChannel(currentId: String, direction: Int): Channel? =
        withContext(Dispatchers.Default) {
            val database = db ?: return@withContext null
            runCatching {
                val q = database.channelQueries
                val cur = q.selectById(currentId).executeAsOneOrNull() ?: return@runCatching null
                val group = cur.groupName?.takeIf { g ->
                    q.countFiltered(g, null).executeAsOne() > 1L
                }
                val next = if (direction >= 0) {
                    q.nextByName(group, cur.name, cur.id).executeAsOneOrNull()
                        ?: q.firstByName(group).executeAsOneOrNull()
                } else {
                    q.prevByName(group, cur.name, cur.id).executeAsOneOrNull()
                        ?: q.lastByName(group).executeAsOneOrNull()
                }
                next?.takeIf { it.id != currentId }?.toDomain()
            }.getOrNull()
        }

    // ──────────────────── Per-source writes (streaming sync) ────────────────────
    // These let the sync persist one source at a time so the full multi-source
    // catalog never has to be accumulated in RAM.

    /** Replace the persisted source list (content rows are managed separately). */
    suspend fun saveSources(sources: List<IptvSource>) = withContext(Dispatchers.Default) {
        val database = db ?: return@withContext
        runCatching {
            database.iptvSourceQueries.transaction {
                database.iptvSourceQueries.deleteAll()
                sources.forEach { s ->
                    when (s) {
                        is IptvSource.M3USource ->
                            database.iptvSourceQueries.insert(s.id, s.name, "m3u", s.url, null, null, null)
                        is IptvSource.XtreamSource ->
                            database.iptvSourceQueries.insert(
                                s.id, s.name, "xtream", null, s.serverUrl, s.username, s.password
                            )
                    }
                }
            }
        }
        Unit
    }

    /** Replace one source's channels + VOD in a single transaction. */
    suspend fun saveSourceContent(
        sourceId: String,
        channels: List<Channel>,
        vod: List<VodItem>
    ) = withContext(Dispatchers.Default) {
        val database = db ?: return@withContext
        runCatching {
            database.channelQueries.transaction {
                database.channelQueries.deleteBySourceId(sourceId)
                database.vodItemQueries.deleteBySourceId(sourceId)
                channels.forEach { c ->
                    database.channelQueries.insert(
                        c.id, c.sourceId, c.name, c.streamUrl, c.logoUrl, c.group, c.streamType.name
                    )
                }
                vod.forEach { v ->
                    database.vodItemQueries.insert(
                        v.id, v.sourceId, v.name, v.streamUrl, v.coverUrl, v.plot, v.genre, v.duration, v.rating, v.group
                    )
                }
            }
        }
        Unit
    }

    /** Replace one source's series rows (used by the progressive lazy series sync). */
    suspend fun saveSeriesForSource(sourceId: String, series: Collection<SeriesItem>) =
        withContext(Dispatchers.Default) {
            val database = db ?: return@withContext
            runCatching {
                database.seriesQueries.transaction {
                    database.seriesQueries.deleteBySourceId(sourceId)
                    series.forEach { s ->
                        database.seriesQueries.insert(
                            s.id, s.sourceId, s.seriesId.toLong(), s.name, s.posterUrl, s.plot,
                            s.cast, s.director, s.genre, s.releaseDate, s.rating, s.group
                        )
                    }
                }
            }
            Unit
        }

    /** Stamp the cache as freshly synced (starts the TTL window). */
    suspend fun markSynced() = withContext(Dispatchers.Default) {
        val database = db ?: return@withContext
        runCatching { database.cacheMetaQueries.upsert(KEY_SYNCED_AT, nowMs().toString()) }
        Unit
    }

    suspend fun save(
        sources: List<IptvSource>,
        channels: List<Channel>,
        vod: List<VodItem>
    ) = withContext(Dispatchers.Default) {
        val database = db ?: return@withContext
        runCatching {
            database.channelQueries.transaction {
                database.iptvSourceQueries.deleteAll()
                database.channelQueries.deleteAll()
                database.vodItemQueries.deleteAll()
                sources.forEach { s ->
                    when (s) {
                        is IptvSource.M3USource ->
                            database.iptvSourceQueries.insert(s.id, s.name, "m3u", s.url, null, null, null)
                        is IptvSource.XtreamSource ->
                            database.iptvSourceQueries.insert(
                                s.id, s.name, "xtream", null, s.serverUrl, s.username, s.password
                            )
                    }
                }
                channels.forEach { c ->
                    database.channelQueries.insert(
                        c.id, c.sourceId, c.name, c.streamUrl, c.logoUrl, c.group, c.streamType.name
                    )
                }
                vod.forEach { v ->
                    database.vodItemQueries.insert(
                        v.id, v.sourceId, v.name, v.streamUrl, v.coverUrl, v.plot, v.genre, v.duration, v.rating, v.group
                    )
                }
                database.cacheMetaQueries.upsert(KEY_SYNCED_AT, nowMs().toString())
            }
        }
        Unit
    }

    /** Marks the cache stale so the next sync re-fetches (e.g. after add/remove). */
    suspend fun invalidate() = withContext(Dispatchers.Default) {
        val database = db ?: return@withContext
        runCatching { database.cacheMetaQueries.upsert(KEY_SYNCED_AT, "0") }
        Unit
    }

    /** Persist the full series catalog (replaces what's there). Series are fetched
     * lazily after the initial live+VOD sync, so they're saved separately. */
    suspend fun saveSeries(series: List<SeriesItem>) = withContext(Dispatchers.Default) {
        val database = db ?: return@withContext
        runCatching {
            database.seriesQueries.transaction {
                database.seriesQueries.deleteAll()
                series.forEach { s ->
                    database.seriesQueries.insert(
                        s.id, s.sourceId, s.seriesId.toLong(), s.name, s.posterUrl, s.plot,
                        s.cast, s.director, s.genre, s.releaseDate, s.rating, s.group
                    )
                }
            }
        }
        Unit
    }

    /** Drop a single source's content rows immediately (used on source removal so
     * Browse stops paging deleted content without waiting for a full re-sync). */
    suspend fun deleteSourceContent(sourceId: String) = withContext(Dispatchers.Default) {
        val database = db ?: return@withContext
        runCatching {
            database.channelQueries.transaction {
                database.channelQueries.deleteBySourceId(sourceId)
                database.vodItemQueries.deleteBySourceId(sourceId)
                database.seriesQueries.deleteBySourceId(sourceId)
            }
        }
        Unit
    }

    private fun IptvSourceEntity.toDomain(): IptvSource? = when (type) {
        "m3u" -> IptvSource.M3USource(id = id, name = name, url = url.orEmpty())
        "xtream" -> IptvSource.XtreamSource(
            id = id, name = name,
            serverUrl = serverUrl.orEmpty(), username = username.orEmpty(), password = password.orEmpty()
        )
        else -> null
    }

    private fun ChannelEntity.toDomain(): Channel = Channel(
        id = id, sourceId = sourceId, name = name, streamUrl = streamUrl,
        logoUrl = logoUrl, group = groupName,
        streamType = runCatching { StreamType.valueOf(streamType) }.getOrDefault(StreamType.HLS)
    )

    private fun VodItemEntity.toDomain(): VodItem = VodItem(
        id = id, sourceId = sourceId, name = name, streamUrl = streamUrl,
        coverUrl = coverUrl, plot = plot, genre = genre, duration = duration, rating = rating, group = groupName
    )

    private fun SeriesEntity.toDomain(): SeriesItem = SeriesItem(
        id = id, sourceId = sourceId, seriesId = seriesId.toInt(), name = name,
        posterUrl = posterUrl, plot = plot, cast = castMembers, director = director,
        genre = genre, releaseDate = releaseDate, rating = rating, group = groupName
    )

    companion object {
        private const val KEY_SYNCED_AT = "content_synced_at"
    }
}
